import React, { forwardRef } from 'react';
import LoaderDots from "@/shared/ui/LoaderDots";
import cx from 'classnames';
import s from './Button.module.scss';

type ButtonProps = {
  className?: string;
  justify?: string;
  onClick?: (...props: any) => void;
  disabled?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'none';
  component?: 'button' | 'a';
  type?: 'button' | 'submit' | 'reset';
  prefixIcon?: React.ReactNode;
  suffixIcon?: React.ReactNode;
  text?: string;
  fw?: 'normal' | 'semibold';
  colorSchema?: 'primary' | 'secondary' | 'plain' | 'link';
  isLoading?: boolean;
  isActive?: boolean;
  isOverlay?: boolean;
  children?: React.ReactNode;
  [x:string]: any;
}

const Button = forwardRef((props: ButtonProps, forwardedRef) => {
  const {
    className = '',
    justify = 'center',
    onClick,
    disabled = false,
    isOverlay = false,
    component: Component = 'button',
    size = 'sm',
    type = 'button',
    text,
    colorSchema = 'secondary',
    fw = 'semibold',
    prefixIcon,
    suffixIcon,
    isLoading = false,
    isActive = false,
    children,
    ...restProps
  } = props;

  const content = text || children;

  const sizes = {
    xs: s.xs,
    sm: s.sm,
    md: s.md,
    none: '',
  };

  const variants = {
    solid: s.solid,
    text: s.text,
    outline: s.outline,
  };

  const colorSchemes = {
    secondary: s.secondary,
    primary: s.primary,
    plain: s.plain,
    link: s.link,
  };

  const buttonClasses = cx(
    s.button,
    disabled && s.disabled,
    colorSchemes[colorSchema],
  );

  const innerClasses = cx(
    s.inner,
    sizes[size],
    s[justify],
    s[fw],
  );

  const componentRestProps = Component === 'button' ? {
    type,
  } : {};

  return (
    <Component
      disabled={disabled}
      className={cx(buttonClasses, className, {
        [s.isActive]: isActive,
        [s.isLoading]: isLoading,
        [s.overlay]: isOverlay,
      })}
      // @ts-ignore
      ref={forwardedRef}
      onClick={onClick}
      {...componentRestProps}
      {...restProps}
    >
      <span className={cx(innerClasses)}>
        {isLoading ? (
          <span className={s.loadingDots}>
            <LoaderDots isLoading isDark={false} />
          </span>
        ) : (
          <>
            {prefixIcon ? (
              <span className={s.prefix}>
                {prefixIcon}
              </span>
            ) : null}
            {content}
            {suffixIcon ? (
              <span className={s.suffix}>
                {suffixIcon}
              </span>
            ) : null}
          </>
        )}
      </span>
    </Component>
  );
})

Button.displayName = 'Button';

export default Button;
